import { useState, useEffect } from 'react';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Button from '@/components/ui/Button';
import {
  SOURCE_TRANSACTION_OPTIONS,
  TYPE_TRANSACTION_OPTIONS,
} from '@/constants/enums';

const EMPTY_FORM = {
  source: '',
  type_transaction: '',
  adresse: '',
  commune: '',
  type_bien: '',
  surface_habitable_m2: '',
  surface_terrain_m2: '',
  prix_euros: '',
  prix_unitaire_euros_m2: '',
  date_transaction: '',
  nombre_pieces: '',
  etat_bien: '',
  observations: '',
};

export default function TransactionFormModal({ transaction, onSave, onCancel }) {
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (transaction) {
      setForm({
        source: transaction.source ?? '',
        type_transaction: transaction.type_transaction ?? '',
        adresse: transaction.adresse ?? '',
        commune: transaction.commune ?? '',
        type_bien: transaction.type_bien ?? '',
        surface_habitable_m2: transaction.surface_habitable_m2 ?? '',
        surface_terrain_m2: transaction.surface_terrain_m2 ?? '',
        prix_euros: transaction.prix_euros ?? '',
        prix_unitaire_euros_m2: transaction.prix_unitaire_euros_m2 ?? '',
        date_transaction: transaction.date_transaction ?? '',
        nombre_pieces: transaction.nombre_pieces ?? '',
        etat_bien: transaction.etat_bien ?? '',
        observations: transaction.observations ?? '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [transaction]);

  // Auto-calcul prix unitaire
  useEffect(() => {
    const prix = parseFloat(form.prix_euros);
    const surface = parseFloat(form.surface_habitable_m2);
    if (prix > 0 && surface > 0) {
      setForm((prev) => ({
        ...prev,
        prix_unitaire_euros_m2: Math.round(prix / surface),
      }));
    } else {
      setForm((prev) => ({ ...prev, prix_unitaire_euros_m2: '' }));
    }
  }, [form.prix_euros, form.surface_habitable_m2]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      surface_habitable_m2: form.surface_habitable_m2 ? parseFloat(form.surface_habitable_m2) : null,
      surface_terrain_m2: form.surface_terrain_m2 ? parseFloat(form.surface_terrain_m2) : null,
      prix_euros: form.prix_euros ? parseFloat(form.prix_euros) : null,
      prix_unitaire_euros_m2: form.prix_unitaire_euros_m2 ? parseFloat(form.prix_unitaire_euros_m2) : null,
      nombre_pieces: form.nombre_pieces ? parseInt(form.nombre_pieces, 10) : null,
      source: form.source || null,
      type_transaction: form.type_transaction || null,
      date_transaction: form.date_transaction || null,
    };
    onSave(payload);
  };

  const isEditing = !!transaction;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">
          {isEditing ? 'Modifier la transaction' : 'Ajouter une transaction'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Source *"
              name="source"
              value={form.source}
              options={SOURCE_TRANSACTION_OPTIONS}
              onChange={handleChange('source')}
              required
            />
            <Select
              label="Type de transaction"
              name="type_transaction"
              value={form.type_transaction}
              options={TYPE_TRANSACTION_OPTIONS}
              onChange={handleChange('type_transaction')}
            />
            <Input
              label="Adresse"
              name="adresse"
              value={form.adresse}
              onChange={handleChange('adresse')}
            />
            <Input
              label="Commune"
              name="commune"
              value={form.commune}
              onChange={handleChange('commune')}
            />
            <Input
              label="Type de bien"
              name="type_bien"
              value={form.type_bien}
              onChange={handleChange('type_bien')}
            />
            <Input
              label="Surface habitable (m2)"
              name="surface_habitable_m2"
              type="number"
              value={form.surface_habitable_m2}
              onChange={handleChange('surface_habitable_m2')}
            />
            <Input
              label="Surface terrain (m2)"
              name="surface_terrain_m2"
              type="number"
              value={form.surface_terrain_m2}
              onChange={handleChange('surface_terrain_m2')}
            />
            <Input
              label="Prix (euros) *"
              name="prix_euros"
              type="number"
              value={form.prix_euros}
              onChange={handleChange('prix_euros')}
              required
            />
            <Input
              label="Prix unitaire (euros/m2)"
              name="prix_unitaire_euros_m2"
              type="number"
              value={form.prix_unitaire_euros_m2}
              disabled
            />
            <Input
              label="Date de transaction"
              name="date_transaction"
              type="date"
              value={form.date_transaction}
              onChange={handleChange('date_transaction')}
            />
            <Input
              label="Nombre de pieces"
              name="nombre_pieces"
              type="number"
              value={form.nombre_pieces}
              onChange={handleChange('nombre_pieces')}
            />
            <Input
              label="Etat du bien"
              name="etat_bien"
              value={form.etat_bien}
              onChange={handleChange('etat_bien')}
            />
          </div>
          <Input
            label="Observations"
            name="observations"
            type="textarea"
            value={form.observations}
            onChange={handleChange('observations')}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={onCancel}>
              Annuler
            </Button>
            <Button type="submit">
              {isEditing ? 'Modifier' : 'Ajouter'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
